import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Paper,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import systemUpdateService, {
  SystemUpdateCheckData,
  SystemUpdateJobStatus,
} from '../services/systemUpdateService';
import { env } from '../utils/env';
import { formatarDataHora } from '../utils/formatToView';

const STEP_LABELS: Record<SystemUpdateJobStatus, string> = {
  pending: 'Aguardando início',
  backing_up: 'Criando backup',
  pulling: 'Baixando nova versão',
  building: 'Compilando serviços',
  migrating: 'Executando migrations',
  restarting: 'Sistema atualizado com sucesso, reiniciando serviços...',
  done: 'Concluído',
  failed: 'Falhou',
};

const STEP_ORDER: SystemUpdateJobStatus[] = [
  'pending',
  'backing_up',
  'pulling',
  'building',
  'migrating',
  'restarting',
  'done',
];

const getStepProgress = (status: SystemUpdateJobStatus | null): number => {
  if (!status) return 0;
  const index = STEP_ORDER.indexOf(status);
  if (index < 0) return 0;
  return Math.round((index / (STEP_ORDER.length - 1)) * 100);
};

const AtualizacaoPage: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;

  const [localVersion, setLocalVersion] = useState<string | null>(null);
  const [loadingVersion, setLoadingVersion] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<SystemUpdateCheckData | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<SystemUpdateJobStatus | null>(null);
  const [currentStepDetail, setCurrentStepDetail] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [activeLogId, setActiveLogId] = useState<number | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadLocalVersion = useCallback(async () => {
    try {
      setLoadingVersion(true);
      const version = await systemUpdateService.getStatus();
      setLocalVersion(version);
    } catch {
      setLocalVersion(null);
    } finally {
      setLoadingVersion(false);
    }
  }, []);

  const checkActiveProgress = useCallback(async () => {
    try {
      const progress = await systemUpdateService.getProgress();
      if (progress.active && progress.logId) {
        setApplying(true);
        setUpdateStatus(progress.status);
        setCurrentStepDetail(progress.currentStepDetail || null);
        setUpdateError(progress.errorMessage);
        setActiveLogId(progress.logId);
        startPolling(progress.logId);
      }
    } catch {
      // Ignora erro inicial de progresso se backend não responder
    }
  }, []);

  useEffect(() => {
    loadLocalVersion();
    checkActiveProgress();
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [loadLocalVersion, checkActiveProgress]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (logId: number) => {
    stopPolling();
    let consecutiveErrors = 0;

    pollRef.current = setInterval(async () => {
      try {
        const progress = await systemUpdateService.getProgress(logId);
        consecutiveErrors = 0;
        setUpdateStatus(progress.status);
        setCurrentStepDetail(progress.currentStepDetail || null);
        setUpdateError(progress.errorMessage);

        if (!progress.active) {
          stopPolling();
          setApplying(false);

          if (progress.status === 'done') {
            toast.success('Atualização concluída com sucesso! Recarregue a página.');
            await loadLocalVersion();
            setCheckResult(null);
          } else if (progress.status === 'failed') {
            toast.error(progress.errorMessage || 'Falha ao aplicar atualização');
          }
        }
      } catch {
        consecutiveErrors += 1;
        // Durante o restart do PM2, o backend pode ficar inacessível por alguns segundos.
        // Tolera até ~75 segundos (25 tentativas x 3s) antes de desistir.
        if (consecutiveErrors > 25) {
          stopPolling();
          setApplying(false);
          toast.error('O servidor demorou para responder após a reinicialização. Tente recarregar a página.');
        }
      }
    }, 3000);
  };

  const handleVerificar = async () => {
    try {
      setChecking(true);
      setCheckError(null);
      setCheckResult(null);

      const result = await systemUpdateService.checkUpdate();
      setCheckResult(result);
      setLocalVersion(result.localVersion);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao verificar atualização';
      setCheckError(message);
    } finally {
      setChecking(false);
    }
  };

  const handleAplicar = async () => {
    if (!checkResult || checkResult.upToDate) return;

    const confirm = await Swal.fire({
      title: 'Aplicar atualização?',
      html: `
        <p>O sistema será atualizado para a versão <strong>v${checkResult.remoteVersion}</strong>.</p>
        <p>Um backup automático será criado antes da atualização.</p>
        <p><strong>Atenção:</strong> o sistema ficará indisponível por alguns minutos durante o processo.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, aplicar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: primario,
    });

    if (!confirm.isConfirmed) return;

    try {
      setApplying(true);
      setUpdateError(null);
      setUpdateStatus('pending');

      const logId = await systemUpdateService.applyUpdate(checkResult.remoteVersion);
      setActiveLogId(logId);
      startPolling(logId);
      toast.info('Atualização iniciada. Acompanhe o progresso abaixo.');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao iniciar atualização';
      setUpdateError(message);
      setApplying(false);
      toast.error(message);
    }
  };

  const handleDestravar = async () => {
    const confirm = await Swal.fire({
      title: 'Destravar atualização?',
      text: 'Isso irá cancelar o status de atualização em andamento no servidor.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, destravar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.error.main,
    });

    if (!confirm.isConfirmed) return;

    try {
      await systemUpdateService.clearStuckUpdate();
      stopPolling();
      setApplying(false);
      setUpdateStatus(null);
      setUpdateError(null);
      setActiveLogId(null);
      toast.success('Atualização destravada com sucesso! Você pode tentar novamente.');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao destravar atualização';
      toast.error(message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Atualização | {env.APP_NAME}</title>
      </Helmet>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Atualização do Sistema
          </Typography>
        </Box>

        <Card sx={{ mb: 3, textAlign: 'center' }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Versão instalada
            </Typography>
            <Chip
              label={
                loadingVersion
                  ? 'Carregando...'
                  : localVersion
                  ? `v${localVersion}`
                  : 'Não identificada'
              }
              color={localVersion ? 'primary' : loadingVersion ? 'default' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '1rem', px: 1 }}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={checking ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleVerificar}
            disabled={checking || applying}
            sx={{ minWidth: 200, px: 4 }}
          >
            {checking ? 'Verificando...' : 'Verificar'}
          </Button>
        </Box>

        {checkError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {checkError}
          </Alert>
        )}

        {checkResult?.upToDate && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Sua versão está atualizada (v{checkResult.localVersion})
          </Alert>
        )}

        {checkResult && !checkResult.upToDate && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              border: `1px solid ${alpha(primario, 0.3)}`,
              backgroundColor: alpha(primario, 0.04),
            }}
          >
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              Nova versão disponível: <strong>v{checkResult.remoteVersion}</strong>
            </Alert>

            {checkResult.publishedAt && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Publicada em: {formatarDataHora(checkResult.publishedAt)}
              </Typography>
            )}

            {checkResult.releaseNotes && (
              <Box sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Release Notes
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    maxHeight: 320,
                    overflow: 'auto',
                    backgroundColor: 'background.paper',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                  }}
                >
                  {checkResult.releaseNotes}
                </Paper>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={applying ? <CircularProgress size={20} color="inherit" /> : <SystemUpdateAltIcon />}
                onClick={handleAplicar}
                disabled={applying}
                sx={{ minWidth: 200, px: 4 }}
              >
                {applying ? 'Aplicando...' : 'Aplicar'}
              </Button>
            </Box>
          </Paper>
        )}

        {(applying || updateStatus) && (
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Progresso da atualização
              </Typography>

              {updateStatus && (
                <>
                  <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    {updateStatus === 'building' && currentStepDetail
                      ? currentStepDetail
                      : STEP_LABELS[updateStatus]}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getStepProgress(updateStatus)}
                    sx={{ mb: 2, height: 8, borderRadius: 1 }}
                  />
                </>
              )}

              {activeLogId && (
                <Typography variant="caption" color="text.secondary">
                  Registro #{activeLogId}
                </Typography>
              )}

              {updateError && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {updateError}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDestravar}
                  >
                    Destravar / Limpar Atualização
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {!applying && updateError && !updateStatus && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleDestravar}
            >
              Destravar Atualização em Andamento
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AtualizacaoPage;
